/**
 * Auth Modal Component
 * 
 * Login/Signup modal with email/password and Google OAuth options.
 * Uses React Portal to render at body level (outside toolbar).
 */

import React, { useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuth } from '../contexts/AuthContext';
import { validateSignupEmail } from '../utils/emailValidation';
import './AuthModal.css';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

/**
 * Detect if the page is running inside an in-app browser (WebView).
 * Google OAuth blocks sign-in from these embedded browsers.
 */
function detectInAppBrowser(): { isInApp: boolean; appName: string } {
  const ua = navigator.userAgent || '';
  if (/FBAN|FBAV/i.test(ua)) return { isInApp: true, appName: 'Facebook' };
  if (/Instagram/i.test(ua)) return { isInApp: true, appName: 'Instagram' };
  if (/LinkedInApp/i.test(ua)) return { isInApp: true, appName: 'LinkedIn' };
  if (/Twitter/i.test(ua)) return { isInApp: true, appName: 'X (Twitter)' };
  if (/Snapchat/i.test(ua)) return { isInApp: true, appName: 'Snapchat' };
  if (/TikTok/i.test(ua)) return { isInApp: true, appName: 'TikTok' };
  // Generic WebView detection (Android WebView or iOS WKWebView without Safari)
  if (/wv\)|WebView/i.test(ua)) return { isInApp: true, appName: 'this app' };
  // iOS: has iPhone/iPad but no "Safari" in UA → likely a WebView
  if (/iPhone|iPad/i.test(ua) && !/Safari/i.test(ua)) return { isInApp: true, appName: 'this app' };
  return { isInApp: false, appName: '' };
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthMode = 'signin' | 'signup' | 'magic-link' | 'forgot-password';

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const { signIn, signUp, signInWithMagicLink, signInWithGoogle, resetPassword, resendConfirmation } = useAuth();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const inAppBrowser = useMemo(() => detectInAppBrowser(), []);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    // Honeypot: bots fill hidden fields, humans don't
    if (honeypot) {
      // Silently pretend success to not tip off the bot
      setLoading(true);
      setTimeout(() => { setLoading(false); setMessage('Check your email!'); }, 1500);
      return;
    }

    setLoading(true);

    try {
      if (mode === 'signin') {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          onClose();
        }
      } else if (mode === 'signup') {
        // Turnstile captcha check (signup only — Supabase enforces captcha on signup,
        // not on signInWithPassword)
        if (TURNSTILE_SITE_KEY && !captchaToken) {
          setError('Please complete the verification check.');
          setLoading(false);
          return;
        }
        // Disposable email check
        const emailError = validateSignupEmail(email);
        if (emailError) {
          setError(emailError);
          setLoading(false);
          return;
        }

        const { error } = await signUp(email, password, captchaToken || undefined);
        // Reset Turnstile after each attempt so user can retry
        turnstileRef.current?.reset();
        setCaptchaToken(null);

        if (error) {
          setError(error.message);
        } else {
          setSignupEmail(email);
          setAwaitingConfirmation(true);
        }
      } else if (mode === 'magic-link') {
        const { error } = await signInWithMagicLink(email);
        if (error) {
          setError(error.message);
        } else {
          setMessage('Check your email for the magic link!');
        }
      } else if (mode === 'forgot-password') {
        const { error } = await resetPassword(email);
        if (error) {
          setError(error.message);
        } else {
          setMessage('Check your email for password reset instructions!');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const { error } = await resendConfirmation(signupEmail);
      if (error) {
        setError(error.message);
      } else {
        setMessage('Email resent! Check your inbox and spam folder.');
      }
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="auth-modal-backdrop" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>×</button>

        {/* ── Post-signup: awaiting email confirmation ── */}
        {awaitingConfirmation ? (
          <>
            <h2 className="auth-modal-title">Check your email</h2>
            <div className="auth-modal-success">
              A confirmation link was sent to <strong>{signupEmail}</strong>.<br />
              Click the link to activate your account.
            </div>
            <p className="auth-modal-hint">
              Can't find it? Check your <strong>spam / junk</strong> folder.
            </p>
            {error && <div className="auth-modal-error">{error}</div>}
            {message && <div className="auth-modal-success">{message}</div>}
            <button
              className="auth-modal-submit"
              onClick={handleResendConfirmation}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Resend confirmation email'}
            </button>
            <div className="auth-modal-divider"><span>or</span></div>
            <button
              className="auth-modal-link-btn"
              onClick={async () => {
                setLoading(true);
                setError(null);
                const { error } = await signInWithMagicLink(signupEmail);
                if (error) setError(error.message);
                else setMessage('Magic link sent! Check your inbox.');
                setLoading(false);
              }}
              disabled={loading}
            >
              Send a magic link instead
            </button>
            <div className="auth-modal-links">
              <button onClick={() => { setAwaitingConfirmation(false); setMode('signin'); }}>
                Back to sign in
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="auth-modal-title">
              {mode === 'signin' && 'Sign In'}
              {mode === 'signup' && 'Create Account'}
              {mode === 'magic-link' && 'Magic Link'}
              {mode === 'forgot-password' && 'Reset Password'}
            </h2>

            {error && (
              <div className="auth-modal-error">
                {error}
                {(error.toLowerCase().includes('google') || error.toLowerCase().includes('already exists')) && (
                  <button
                    className="auth-modal-error-action"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                  >
                    Sign in with Google →
                  </button>
                )}
              </div>
            )}
            {message && <div className="auth-modal-success">{message}</div>}

            <form onSubmit={handleSubmit} className="auth-modal-form">
              <div className="auth-modal-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              {(mode === 'signin' || mode === 'signup') && (
                <div className="auth-modal-field">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              )}

              {/* Honeypot field — invisible to humans, bots fill it */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {/* Turnstile CAPTCHA — signup only (Supabase validates captcha on signup endpoint) */}
              {mode === 'signup' && TURNSTILE_SITE_KEY && (
                <div className="auth-modal-turnstile">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                    options={{
                      theme: 'dark',
                      size: 'flexible',
                    }}
                  />
                </div>
              )}

              <button
                type="submit"
                className="auth-modal-submit"
                disabled={loading || (mode === 'signup' && !!TURNSTILE_SITE_KEY && !captchaToken)}
              >
                {loading ? 'Loading...' : (
                  mode === 'signin' ? 'Sign In' :
                  mode === 'signup' ? 'Create Account' :
                  mode === 'magic-link' ? 'Send Magic Link' :
                  'Send Reset Email'
                )}
              </button>
            </form>

            {(mode === 'signin' || mode === 'signup') && (
              <>
                <div className="auth-modal-divider">
                  <span>or</span>
                </div>

                {inAppBrowser.isInApp ? (
                  <div className="auth-modal-webview-warning">
                    <p>Google sign-in is not available in {inAppBrowser.appName}'s browser.</p>
                    <button
                      className="auth-modal-open-browser"
                      onClick={() => {
                        // Attempt to open in system browser
                        window.open(window.location.href, '_system');
                      }}
                    >
                      Open in Browser
                    </button>
                    <p className="auth-modal-webview-hint">
                      Or use <strong>email sign-in</strong> above, or tap <strong>⋯</strong> → <strong>"Open in Browser"</strong>
                    </p>
                  </div>
                ) : (
                  <button
                    className="auth-modal-google"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>
                )}
              </>
            )}

            <div className="auth-modal-links">
              {mode === 'signin' && (
                <>
                  <button onClick={() => setMode('forgot-password')}>Forgot password?</button>
                  <button onClick={() => setMode('magic-link')}>Use magic link</button>
                  <button onClick={() => setMode('signup')}>Create account</button>
                </>
              )}
              {mode === 'signup' && (
                <button onClick={() => setMode('signin')}>Already have an account?</button>
              )}
              {(mode === 'magic-link' || mode === 'forgot-password') && (
                <button onClick={() => setMode('signin')}>Back to sign in</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Use portal to render modal at body level (outside toolbar)
  return createPortal(modalContent, document.body);
};

export default AuthModal;
